import { User, Mail, MapPin, Calendar, UserCheck, Euro, Users } from "lucide-react";
import { useI18n } from '@/hooks/useI18n';
import { DashboardItinerary } from "@/lib/models/dashboard";

interface CustomerInfoProps {
    itinerary: DashboardItinerary;
    compact?: boolean;
}

export const CustomerInfo = ({ itinerary, compact = false }: CustomerInfoProps) => {
    const { t } = useI18n();

    if (!itinerary.travelRequestId) return null;

    // Compact version: single line with key info
    if (compact) {
        return (
            <div className="rounded-md bg-gray-50 border border-gray-200 px-3 py-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
                <span className="flex items-center gap-1.5 font-medium text-gray-900">
                    <User className="h-3.5 w-3.5 text-gray-500" />
                    {itinerary.contactFullName}
                </span>
                <span className="flex items-center gap-1.5 text-gray-600">
                    <Mail className="h-3 w-3 text-gray-400" />
                    {itinerary.contactEmail}
                </span>
                <span className="text-gray-300">•</span>
                <span className="flex items-center gap-1.5 text-gray-700">
                    <MapPin className="h-3 w-3 text-gray-500" />
                    {itinerary.title}
                </span>
                <span className="flex items-center gap-1.5 text-gray-600">
                    <Calendar className="h-3 w-3 text-gray-400" />
                    {itinerary.startDate} – {itinerary.endDate}
                </span>
                {itinerary.totalGuests > 0 && (
                    <>
                        <span className="text-gray-300">•</span>
                        <span className="flex items-center gap-1.5 text-gray-600">
                            <Users className="h-3 w-3 text-gray-400" />
                            {itinerary.totalGuests} {itinerary.totalGuests === 1 ? t('common.guest') : t('common.guests')}
                        </span>
                    </>
                )}
                {itinerary.total != null && (
                    <>
                        <span className="text-gray-300">•</span>
                        <span className="flex items-center gap-1.5 text-gray-600 font-medium">
                            <Euro className="h-3 w-3 text-gray-500" />
                            {itinerary.total.toLocaleString('it-IT', { minimumFractionDigits: 2 })}
                        </span>
                    </>
                )}
            </div>
        );
    }

    // Full version: detailed layout

    // Full version: detailed layout
    return (
        <div className="rounded-lg bg-gray-50 border border-gray-200 px-5 py-4 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
                {/* Client */}
                <div className="space-y-1.5">
                    <h6 className="text-xs font-medium tracking-wide text-gray-400 uppercase mb-2">
                        {t('common.client') || "Client"}
                    </h6>
                    <span className="flex items-center gap-2 text-base font-semibold text-gray-900">
                        <User className="h-4 w-4 text-gray-500" />
                        {itinerary.contactFullName}
                    </span>
                    <span className="flex items-center gap-2 text-sm text-gray-700">
                        <Mail className="h-3.5 w-3.5 text-gray-500" />
                        {itinerary.contactEmail}
                    </span>
                </div>
                {/* Advisor */}
                <div className="space-y-1.5">
                    <h6 className="text-xs font-medium tracking-wide text-gray-400 uppercase mb-2">
                        {t('common.advisor') || "Advisor"}
                    </h6>
                    <span className="flex items-center gap-2 text-base font-semibold text-gray-900">
                        <UserCheck className="h-4 w-4 text-gray-500" />
                        {itinerary.assignedAdvisorFullName || t('common.notAssigned') || 'Not assigned'}
                    </span>
                    {itinerary.assignedAdvisor?.email && (
                        <span className="flex items-center gap-2 text-sm text-gray-700">
                            <Mail className="h-3.5 w-3.5 text-gray-500" />
                            {itinerary.assignedAdvisor.email}
                        </span>
                    )}
                </div>
            </div>
            {/* Trip */}
            <div className="border-t border-gray-200 pt-3 flex flex-wrap items-center gap-x-6 gap-y-2">
                <h6 className="text-xs font-medium tracking-wide text-gray-400 uppercase">
                    {t('common.trip') || "Trip"}
                </h6>
                <span className="flex items-center gap-2 text-sm text-gray-700">
                    <MapPin className="h-3.5 w-3.5 text-gray-500" />
                    {itinerary.title}
                </span>
                <span className="flex items-center gap-2 text-sm text-gray-700">
                    <Calendar className="h-3.5 w-3.5 text-gray-500" />
                    {itinerary.startDate} – {itinerary.endDate}
                </span>
                {itinerary.totalGuests > 0 && (
                    <span className="flex items-center gap-2 text-sm text-gray-700">
                        <Users className="h-3.5 w-3.5 text-gray-500" />
                        {itinerary.totalGuests} {itinerary.totalGuests === 1 ? t('common.guest') || 'guest' : t('common.guests') || 'guests'}
                    </span>
                )}
                {itinerary.total != null && (
                    <span className="flex items-center gap-2 text-sm text-gray-700">
                        <Euro className="h-3.5 w-3.5 text-gray-500" />
                        {itinerary.total.toLocaleString('it-IT', { minimumFractionDigits: 2 })}
                    </span>
                )}
            </div>
        </div>
    );
};
